import { BadgeQuestionMark, MessageCircleQuestionMark, X } from "lucide-react";
import { FloatingPanel, FloatingPanelContent, FloatingPanelHeader, FloatingPanelSeparator } from "../ui/floating-panel";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { ContextualTip, useContextualTips } from "./contextual-tips-context";
import { cn } from "../../lib/utils";
import { AnimatePresence, motion } from "motion/react";


export type ContextualTipsPanelProps = {
  title?: string;
  defaultTips?: ContextualTip[];
  removeIfHidden?: boolean;
}


function ContextualTipsPanel({ title, defaultTips, removeIfHidden }: ContextualTipsPanelProps) {
  const { tips, isHidden, setIsHidden } = useContextualTips();
  const resolvedTips = (tips?.length ? tips : defaultTips) || [];

  if (!resolvedTips.length) return null;

  if (isHidden && removeIfHidden) return null;

  return (
    <FloatingPanel className={isHidden ? "w-[24px] rounded-full p-0 cursor-pointer" : ""}>
      <FloatingPanelHeader>
        <div className="w-full flex items-center justify-between gap-2">
          <div className={cn("flex items-center gap-2", isHidden && "hidden")}>
            {title || "Tips"}
            <MessageCircleQuestionMark size={14} className="transition-opacity" style={{
              opacity: tips.length ? 1 : 0,
            }} />
          </div>
          <div
            role="button"
            className="cursor-pointer"
            onClick={() => setIsHidden(!isHidden)}
          >
            {isHidden ? <BadgeQuestionMark size={16} /> : <X size={14} />}
          </div>
        </div>
      </FloatingPanelHeader>
      <FloatingPanelSeparator className={isHidden ? "hidden" : ""} />
      <FloatingPanelContent className={isHidden ? "hidden" : ""}>
        <AnimatePresence mode="popLayout">
          {resolvedTips.map(tip => (
            <motion.div
              key={tip.id}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              layout
            >
              <Alert variant={tip.variant || "default"} className={
                resolvedTips.length > 1 && !tip.variant
                  ? ""
                  : "border-0 p-1"
              }>
                {!!tip.title &&
									<AlertTitle className="flex flex-center gap-1">
                    {!!tip.Icon && <tip.Icon size={14} className="" />}
                    {tip.title}
									</AlertTitle>
                }
                <AlertDescription>
                  {tip.description}
                </AlertDescription>
              </Alert>
            </motion.div>
          ))}
        </AnimatePresence>
      </FloatingPanelContent>
    </FloatingPanel>
  );
}


export default ContextualTipsPanel;
